import apiClient from "../services/apiClient";
import { QueryClient, useQuery } from "@tanstack/react-query";

export type AccountStage = 'prospect' | 'trial' | 'live' | 'churned' | 'lost';
export type AccountStatus = 'prospect' | 'trial' | 'trial_expired' | 'live' | 'expired' | 'churned' | 'lost';
export type KeyDateKind = 'trial_ends' | 'ends' | 'ended';

/** The account (via one of the user's teams) that best describes them right now. */
export interface IAdminUserAccount {
	account_id: number;
	account_name: string;
	team_id: number;
	team_name: string;
	stage: AccountStage;
	start_date: string | null;
	end_date: string | null;
	status: AccountStatus;
}

export interface IAdminUserTeamRef {
	id: number;
	name: string;
	organization_id: number;
	organization_name: string;
}

export interface IAdminUser {
	id: number;
	full_name: string | null;
	email: string;
	email_domain: string;
	is_active: boolean;
	is_superuser: boolean;
	is_admin: boolean;
	created_at: string | null;
	/** most recent of last page view and last MCP call */
	last_activity_date: string | null;
	last_page_view: string | null;
	last_mcp_call: string | null;
	mcp_calls_7d: number;
	mcp_calls_30d: number;
	views_count: number;
	tables_count: number;
	screeners_count: number;
	welcome_email_sent: string | null;
	teams: IAdminUserTeamRef[];
	account: IAdminUserAccount | null;
	/** platform = has an Auth0 login; the other two are flags on the user row */
	has_platform_access: boolean;
	mcp_enabled: boolean;
	quant_feed_enabled: boolean;
	/** vendors (data suppliers, not clients) are not expected to have an account */
	is_vendor: boolean;
}

export interface IAdminWatchlist {
	id: number;
	name: string;
	owner_id: number;
	created_at: string | null;
	shared_from_email: string | null;
	is_sync_share: boolean | null;
	admin_shared_by_email: string | null;
}

export interface IAdminDatasetPermission {
	id: number;
	dataset_id: number;
	dataset_name: string;
	vendor_name: string;
	is_active: boolean;
	is_platform_included: boolean;
	internal_description: string | null;
	access_from: string;
	updated_at: string;
	company_id_subset: number[] | null;
	ticker_subset: string | null;
}

export function useAdminUsersQuery(options?: { enabled?: boolean }) {
	async function getUsers() {
		const { data } = await apiClient.get<IAdminUser[]>("/admin/users");
		return data;
	}
	return useQuery<IAdminUser[]>(["admin-users"], getUsers, {
		refetchOnWindowFocus: false,
		enabled: options?.enabled ?? true,
	});
}

/** Single user by id. Shares the "admin-users" key prefix so membership changes refresh it too. */
export function useAdminUserQuery(userId: number) {
	return useQuery<IAdminUser>(
		['admin-users', userId],
		async () => {
			const { data } = await apiClient.get<IAdminUser>(`/admin/users/${userId}`);
			return data;
		},
		{ enabled: !!userId, refetchOnWindowFocus: false },
	);
}

export function useAdminUserWatchlistsQuery(userId: number) {
	async function getWatchlists() {
		const { data } = await apiClient.get<IAdminWatchlist[]>(
			`/admin/users/${userId}/watchlists`
		);
		return data;
	}
	return useQuery<IAdminWatchlist[]>(
		["admin-user-watchlists", userId],
		getWatchlists,
		{ enabled: !!userId, refetchOnWindowFocus: false }
	);
}

export interface IAdminWatchlistCompany {
	id: number;
	symbol: string;
	name: string | null;
	sector: string | null;
	industry: string | null;
	parent_company_id: number | null;
}

export function useAdminWatchlistCompaniesQuery(watchlistId: number) {
	async function getCompanies() {
		const { data } = await apiClient.get<IAdminWatchlistCompany[]>(
			`/admin/watchlists/${watchlistId}/companies`
		);
		return data;
	}
	return useQuery<IAdminWatchlistCompany[]>(
		["admin-watchlist-companies", watchlistId],
		getCompanies,
		{ enabled: !!watchlistId, refetchOnWindowFocus: false }
	);
}

export function useAdminUserDatasetPermissionsQuery(userId: number) {
	async function getPermissions() {
		const { data } = await apiClient.get<IAdminDatasetPermission[]>(
			`/admin/users/${userId}/dataset-permissions`
		);
		return data;
	}
	return useQuery<IAdminDatasetPermission[]>(
		["admin-user-dataset-permissions", userId],
		getPermissions,
		{ enabled: !!userId, refetchOnWindowFocus: false }
	);
}

export interface IAdminDataset {
	id: number;
	name: string;
	vendor_name: string;
	is_platform_included: boolean | null;
	is_demo: boolean | null;
	internal_description: string | null;
}

export interface IAdminSymbolDataset extends IAdminDataset {
	has_symbols: boolean;
	ds_types: string[];
}

async function getAdminDatasets() {
	const { data } = await apiClient.get<IAdminDataset[]>("/admin/datasets");
	return data;
}

export function useAdminDatasetsQuery() {
	return useQuery<IAdminDataset[]>(["admin-datasets"], getAdminDatasets, {
		refetchOnWindowFocus: false,
	});
}

/** A user holding an active permission on a dataset, as listed from the dataset side. */
export interface IAdminDatasetUserPermission {
	id: number;
	user_id: number;
	email: string;
	full_name: string | null;
	organization_names: string | null;
	team_names: string | null;
	/** Also the date the permission was added: every grant path stamps it with the grant time. */
	access_from: string;
	updated_at: string;
	company_id_subset: number[] | null;
	ticker_subset: string | null;
}

export function useAdminDatasetPermissionsQuery(datasetId: number) {
	return useQuery<IAdminDatasetUserPermission[]>(
		['admin', 'datasets', datasetId, 'permissions'],
		async () => {
			const { data } = await apiClient.get<IAdminDatasetUserPermission[]>(
				`/admin/datasets/${datasetId}/permissions`
			);
			return data;
		},
		{ enabled: !!datasetId, refetchOnWindowFocus: false }
	);
}

async function getAdminSymbolDatasets() {
	const { data } = await apiClient.get<IAdminSymbolDataset[]>(
		'/admin/symbols/datasets',
	);
	return data;
}

export function useAdminSymbolDatasetsQuery() {
	return useQuery<IAdminSymbolDataset[]>(
		['admin', 'symbols', 'datasets'],
		getAdminSymbolDatasets,
		{
			refetchOnWindowFocus: false,
		},
	);
}

export async function removeAdminDatasetPermission(
	userId: number,
	permissionId: number
): Promise<void> {
	await apiClient.delete(`/admin/users/${userId}/dataset-permissions/${permissionId}`);
}

/** An active company with data in a dataset: the pool a permission's company subset is picked from. */
export interface IAdminDatasetCompany {
	id: number;
	symbol: string;
	name: string | null;
	sector: string | null;
	industry: string | null;
}

export function useAdminDatasetCompaniesQuery(datasetId: number | null) {
	return useQuery<IAdminDatasetCompany[]>(
		['admin', 'datasets', datasetId, 'companies'],
		async () => {
			const { data } = await apiClient.get<IAdminDatasetCompany[]>(
				`/admin/datasets/${datasetId}/companies`
			);
			return data;
		},
		{ enabled: datasetId !== null, refetchOnWindowFocus: false }
	);
}

/** Replace the company subset on one permission; `null` clears it (full universe). */
export async function updateAdminDatasetPermissionCompanySubset(
	userId: number,
	permissionId: number,
	companyIdSubset: number[] | null
): Promise<IAdminDatasetPermission> {
	const { data } = await apiClient.put<IAdminDatasetPermission>(
		`/admin/users/${userId}/dataset-permissions/${permissionId}/company-subset`,
		{ company_id_subset: companyIdSubset }
	);
	return data;
}

export interface IAdminPageView {
	page: string;
	info: string | null;
	created_at: string;
}

export function useAdminUserPageViewsQuery(userId: number) {
	return useQuery<IAdminPageView[]>(['admin', 'users', userId, 'page-views'], async () => {
		const { data } = await apiClient.get<IAdminPageView[]>(`/admin/users/${userId}/page-views`);
		return data;
	});
}

/** Just the most recent `limit` page views (for quick peeks); cached separately from the full list. */
export function useAdminUserRecentPageViewsQuery(userId: number, limit: number) {
	return useQuery<IAdminPageView[]>(
		['admin', 'users', userId, 'page-views', { limit }],
		async () => {
			const { data } = await apiClient.get<IAdminPageView[]>(`/admin/users/${userId}/page-views`, { params: { limit } });
			return data;
		},
		{ enabled: !!userId, refetchOnWindowFocus: false },
	);
}

export interface IAdminView {
	id: number;
	name: string;
	display_type: string;
	period_lookback: string;
	created_at: string | null;
	last_viewed: string | null;
	inputs: string[];
	universe: string[];
	shared_from_email: string | null;
	is_sync_share: boolean | null;
	admin_shared_by_email: string | null;
}

export function useAdminUserViewsQuery(userId: number) {
	return useQuery<IAdminView[]>(['admin', 'users', userId, 'views'], async () => {
		const { data } = await apiClient.get<IAdminView[]>(`/admin/users/${userId}/views`);
		return data;
	});
}

export interface IAdminWorksheet {
	id: number;
	name: string;
	period: string;
	period_compares: number[] | null;
	group_by: string;
	color_format: string | null;
	trend_summary_period: number | null;
	created_at: string | null;
	last_viewed: string | null;
	inputs: string[];
	universe: string[];
	shared_from_email: string | null;
	is_sync_share: boolean | null;
	admin_shared_by_email: string | null;
}

export function useAdminUserWorksheetsQuery(userId: number) {
	return useQuery<IAdminWorksheet[]>(['admin', 'users', userId, 'worksheets'], async () => {
		const { data } = await apiClient.get<IAdminWorksheet[]>(`/admin/users/${userId}/worksheets`);
		return data;
	});
}

export interface IAdminPeriodRow {
	id: number;
	symbol: string;
	fs_period: string;
	period_start: string;
	period_end: string;
	periodicity: string;
	period_type: string;
	source: string;
	override_notes: string | null;
}

export function useAdminCompanyPeriodsQuery(companyId: number | null) {
	return useQuery<IAdminPeriodRow[]>(
		['admin', 'periods', companyId],
		async () => {
			const { data } = await apiClient.get<IAdminPeriodRow[]>(`/admin/periods/${companyId}`);
			return data;
		},
		{ enabled: companyId !== null },
	);
}

export interface IAdminDashboardComponent {
	id: number;
	type: string;
	size: number | null;
	order: number | null;
	view_id: number | null;
	info: string | null;
}

export function useAdminUserDashboardComponentsQuery(userId: number) {
	return useQuery<IAdminDashboardComponent[]>(
		['admin', 'users', userId, 'dashboard-components'],
		async () => {
			const { data } = await apiClient.get<IAdminDashboardComponent[]>(
				`/admin/users/${userId}/dashboard-components`
			);
			return data;
		},
		{ enabled: !!userId, refetchOnWindowFocus: false },
	);
}

export interface IAdminDashboardComponentCreate {
	type: string;
	order: number;
	size: number | null;
	view_id: number | null;
}

export async function createAdminDashboardComponent(
	userId: number,
	payload: IAdminDashboardComponentCreate
): Promise<IAdminDashboardComponent> {
	const { data } = await apiClient.post<IAdminDashboardComponent>(
		`/admin/users/${userId}/dashboard-components`,
		payload
	);
	return data;
}

export async function deleteAdminDashboardComponent(
	userId: number,
	componentId: number
): Promise<void> {
	await apiClient.delete(`/admin/users/${userId}/dashboard-components/${componentId}`);
}

export function useAdminDashboardComponentTypesQuery() {
	return useQuery<string[]>(
		['admin', 'dashboard-component-types'],
		async () => {
			const { data } = await apiClient.get<string[]>('/admin/dashboard-component-types');
			return data;
		},
		{ refetchOnWindowFocus: false },
	);
}

export interface IAdminEmailReport {
	id: number;
	name: string;
	report_type: string;
	report_params: Record<string, unknown> | null;
	weekday_schedule: number[] | null;
	watchlist_names: string[];
	delta_days: number | null;
	top_n: number;
	include_attachment: boolean | null;
	created_at: string | null;
	field_name: string | null;
}

export function useAdminUserEmailReportsQuery(userId: number) {
	return useQuery<IAdminEmailReport[]>(
		['admin', 'users', userId, 'email-reports'],
		async () => {
			const { data } = await apiClient.get<IAdminEmailReport[]>(
				`/admin/users/${userId}/email-reports`
			);
			return data;
		},
		{ enabled: !!userId, refetchOnWindowFocus: false },
	);
}

export interface IAdminScreener {
	id: number;
	name: string;
	delta_days: number | null;
	top_n: number | null;
	updated_at: string | null;
	inputs: string[];
	universe: string[];
	shared_from_email: string | null;
	is_sync_share: boolean | null;
	admin_shared_by_email: string | null;
}

export function useAdminUserScreenersQuery(userId: number) {
	return useQuery<IAdminScreener[]>(
		['admin', 'users', userId, 'screeners'],
		async () => {
			const { data } = await apiClient.get<IAdminScreener[]>(`/admin/users/${userId}/screeners`);
			return data;
		},
		{ enabled: !!userId, refetchOnWindowFocus: false },
	);
}

export interface ISharedWatchlistResult {
	new_watchlist_id: number;
	new_watchlist_name: string;
	target_user_id: number;
	shared_resource_id: number;
}

export async function adminShareWatchlist(
	sourceWatchlistId: number,
	targetUserId: number,
	newName?: string,
	overwrite?: boolean
): Promise<ISharedWatchlistResult> {
	const { data } = await apiClient.post<ISharedWatchlistResult>('/admin/sharing/watchlist', {
		source_watchlist_id: sourceWatchlistId,
		target_user_id: targetUserId,
		...(newName ? { new_name: newName } : {}),
		...(overwrite ? { overwrite: true } : {}),
	});
	return data;
}

export async function adminShareView(
	sourceViewId: number,
	targetUserId: number,
	newName?: string,
	overwrite?: boolean,
	skipChecks?: boolean
): Promise<void> {
	await apiClient.post('/admin/sharing/view', {
		source_resource_id: sourceViewId,
		target_user_id: targetUserId,
		...(newName ? { new_name: newName } : {}),
		...(overwrite ? { overwrite: true } : {}),
		...(skipChecks ? { skip_checks: true } : {}),
	});
}

export async function adminShareScreener(
	sourceScreenerId: number,
	targetUserId: number,
	newName?: string,
	overwrite?: boolean,
	skipChecks?: boolean
): Promise<void> {
	await apiClient.post('/admin/sharing/screener', {
		source_resource_id: sourceScreenerId,
		target_user_id: targetUserId,
		...(newName ? { new_name: newName } : {}),
		...(overwrite ? { overwrite: true } : {}),
		...(skipChecks ? { skip_checks: true } : {}),
	});
}

export async function adminShareWorksheet(
	sourceWorksheetId: number,
	targetUserId: number,
	newName?: string,
	overwrite?: boolean,
	skipChecks?: boolean
): Promise<void> {
	await apiClient.post('/admin/sharing/worksheet', {
		source_resource_id: sourceWorksheetId,
		target_user_id: targetUserId,
		...(newName ? { new_name: newName } : {}),
		...(overwrite ? { overwrite: true } : {}),
		...(skipChecks ? { skip_checks: true } : {}),
	});
}

export interface ISharedDashboardComponentsResult {
	created_count: number;
	target_user_id: number;
}

export async function adminShareDashboardComponents(
	sourceUserId: number,
	componentIds: number[],
	targetUserId: number
): Promise<ISharedDashboardComponentsResult> {
	const { data } = await apiClient.post<ISharedDashboardComponentsResult>(
		'/admin/sharing/dashboard-components',
		{
			source_user_id: sourceUserId,
			component_ids: componentIds,
			target_user_id: targetUserId,
		}
	);
	return data;
}

export async function bulkAddDatasetPermissions(
	datasetIds: number[],
	userIds: number[],
	accessFrom: string,
): Promise<IAdminDatasetPermission[]> {
	const { data } = await apiClient.post<IAdminDatasetPermission[]>('/admin/datasets/bulk-permissions', {
		dataset_ids: datasetIds,
		user_ids: userIds,
		access_from: new Date(accessFrom).toISOString(),
	});
	return data;
}

export interface INewUserCreate {
	full_name: string;
	email: string;
	is_vendor: boolean;
	is_marketing_recipient: boolean;
}

export async function createUsers(users: INewUserCreate[]): Promise<void> {
	await apiClient.post('/admin/users/bulk-create', { users });
}

export async function sendWelcomeEmails(userIds: number[], testSend: boolean): Promise<{ sent_count: number }> {
	const { data } = await apiClient.post<{ sent_count: number }>('/admin/users/welcome-email', {
		user_ids: userIds,
		test_send: testSend,
	});
	return data;
}

export interface IAdminUserDeactivateResult {
	user_id: number;
	email: string;
	/** False when the user had no Auth0 identity, so there was no platform login to block. */
	auth0_blocked: boolean;
}

/** Deactivate one user: is_active = false plus an Auth0 block, all or nothing on the backend. */
export async function deactivateAdminUser(userId: number): Promise<IAdminUserDeactivateResult> {
	const { data } = await apiClient.post<IAdminUserDeactivateResult>(`/admin/users/${userId}/deactivate`);
	return data;
}

export interface ISyncResourcesResult {
	source_user_id: number;
	target_user_id: number;
	success_count: number;
	skipped_count: number;
	error_count: number;
}

export async function adminSyncResources(
	sourceUserId: number,
	targetUserIds: number[],
	resourceTypes: string[],
): Promise<ISyncResourcesResult[]> {
	const results = await Promise.all(
		targetUserIds.map(async (targetUserId) => {
			const { data } = await apiClient.post<ISyncResourcesResult>('/admin/sharing/sync', {
				source_user_id: sourceUserId,
				target_user_id: targetUserId,
				resource_types: resourceTypes,
			});
			return data;
		})
	);
	return results;
}

export interface ISyncConfigView {
	id: number;
	source_user_id: number;
	target_user_id: number;
	source_user_email: string | null;
	target_user_email: string | null;
	resource_types: string[];
	is_active: boolean;
	created_at: string | null;
	updated_at: string | null;
}

export function useAdminUserSyncConfigsQuery(userId: number) {
	return useQuery<ISyncConfigView[]>(
		['admin', 'users', userId, 'sync-configs'],
		async () => {
			const { data } = await apiClient.get<ISyncConfigView[]>(
				`/admin/sharing/sync-config/${userId}`
			);
			return data;
		},
		{ enabled: !!userId, refetchOnWindowFocus: false },
	);
}

export async function updateSyncConfig(
	configId: number,
	payload: { resource_types?: string[]; is_active?: boolean },
): Promise<ISyncConfigView> {
	const { data } = await apiClient.patch<ISyncConfigView>(
		`/admin/sharing/sync-config/${configId}`,
		payload,
	);
	return data;
}

export async function createResourceSyncConfig(
	sourceUserId: number,
	targetUserIds: number[],
	resourceTypes: string[],
): Promise<void> {
	await apiClient.post('/admin/sharing/sync-config', {
		source_user_id: sourceUserId,
		target_user_ids: targetUserIds,
		resource_types: resourceTypes,
	});
}

export async function copyAdminDatasetPermissions(
	targetUserId: number,
	sourceUserId: number,
	accessFrom: string,
): Promise<IAdminDatasetPermission[]> {
	const { data } = await apiClient.post<IAdminDatasetPermission[]>(
		`/admin/users/${targetUserId}/copy-dataset-permissions`,
		{
			source_user_id: sourceUserId,
			access_from: new Date(accessFrom).toISOString(),
		}
	);
	return data;
}
export const ADMIN_SYMBOL_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export type AdminSymbolPageSize = typeof ADMIN_SYMBOL_PAGE_SIZE_OPTIONS[number];
export const ADMIN_SYMBOL_MATRIX_SEARCH_MAX_LENGTH = 100;

export function isAdminSymbolPageSize(value: unknown): value is AdminSymbolPageSize {
	return typeof value === 'number'
		&& ADMIN_SYMBOL_PAGE_SIZE_OPTIONS.includes(value as AdminSymbolPageSize);
}

function serializeAdminQueryParams(params: Record<string, unknown>) {
	const searchParams = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (Array.isArray(value)) {
			value.forEach((item) => searchParams.append(key, String(item)));
		} else if (value !== undefined && value !== null) {
			searchParams.set(key, String(value));
		}
	});
	return searchParams.toString();
}

export interface IAdminSymbol {
	row_id: string;
	dataset_symbol_id: number;
	dataset_id: number;
	dataset_name: string;
	ds_symbol_1: string | null;
	ds_symbol_2: string | null;
	ds_col_1: string | null;
	ds_col_2: string | null;
	ds_type: string | null;
	symbol_metadata: Record<string, unknown> | null;
	first_seen: string | null;
	last_seen: string | null;
	dataset_symbol_batch_id: number | null;
	dataset_symbol_updated_at: string | null;
	company_id: number | null;
	company_name: string | null;
	company_symbol: string | null;
	company_bloomberg_ticker: string | null;
	parent_company_id: number | null;
	parent_company_symbol: string | null;
	parent_company_name: string | null;
	is_active: boolean | null;
	start_date: string | null;
	end_date: string | null;
	mapping_batch_id: number | null;
	mapping_updated_at: string | null;
}

export type AdminSymbolMappingStatus = 'any' | 'active' | 'inactive' | 'unmapped';

export type AdminSymbolSortField = Exclude<keyof IAdminSymbol, 'row_id'>;

export type AdminSymbolFilterMode = 'include' | 'exclude';

export interface IAdminSymbolFilters {
	datasetIds: number[];
	firstSeenFrom: string;
	firstSeenTo: string;
	firstSeenMode: AdminSymbolFilterMode;
	lastSeenFrom: string;
	lastSeenTo: string;
	lastSeenMode: AdminSymbolFilterMode;
	companyName: string;
	companyNameMode: AdminSymbolFilterMode;
	mappingStatus: AdminSymbolMappingStatus;
	dsTypes: string[];
}

export interface IAdminSymbolSort {
	field: AdminSymbolSortField;
	direction: 'asc' | 'desc';
}

export interface IAdminSymbolsQuery {
	search: string;
	filters: IAdminSymbolFilters;
	sort: IAdminSymbolSort | null;
}

export interface IAdminSymbolsResult {
	items: IAdminSymbol[];
	total: number;
	page: number;
	page_size: AdminSymbolPageSize;
}

export function useAdminSymbolsQuery(
	page: number,
	pageSize: AdminSymbolPageSize,
	query: IAdminSymbolsQuery,
) {
	const normalizedSearch = query.search.trim();
	const trimmedSearch = normalizedSearch.length >= 2 ? normalizedSearch : '';
	const { filters } = query;
	const trimmedCompanyName = filters.companyName.trim();

	return useQuery<IAdminSymbolsResult>(
		['admin', 'symbols', page, pageSize, trimmedSearch, filters, query.sort],
		async () => {
			const { data } = await apiClient.get<IAdminSymbolsResult>('/admin/symbols', {
				params: {
					page,
					page_size: pageSize,
					...(trimmedSearch ? { search: trimmedSearch } : {}),
					...(filters.datasetIds.length > 0
						? { dataset_ids: filters.datasetIds }
						: {}),
					...(filters.mappingStatus !== 'any'
						? { mapping_status: filters.mappingStatus }
						: {}),
					...(filters.dsTypes.length > 0 ? { ds_types: filters.dsTypes } : {}),
					...(filters.firstSeenFrom ? { first_seen_from: filters.firstSeenFrom } : {}),
					...(filters.firstSeenTo ? { first_seen_to: filters.firstSeenTo } : {}),
					...(filters.firstSeenFrom || filters.firstSeenTo
						? { exclude_first_seen: filters.firstSeenMode === 'exclude' || undefined }
						: {}),
					...(filters.lastSeenFrom ? { last_seen_from: filters.lastSeenFrom } : {}),
					...(filters.lastSeenTo ? { last_seen_to: filters.lastSeenTo } : {}),
					...(filters.lastSeenFrom || filters.lastSeenTo
						? { exclude_last_seen: filters.lastSeenMode === 'exclude' || undefined }
						: {}),
					...(trimmedCompanyName ? { company_name: trimmedCompanyName } : {}),
					...(trimmedCompanyName
						? { exclude_company_name: filters.companyNameMode === 'exclude' || undefined }
						: {}),
					...(query.sort
						? {
							sort_field: query.sort.field,
							sort_direction: query.sort.direction,
						}
						: {}),
				},
				paramsSerializer: serializeAdminQueryParams,
			});
			return data;
		},
		{
			refetchOnWindowFocus: false,
			keepPreviousData: true,
			enabled: filters.datasetIds.length > 0,
		},
	);
}


export interface IAdminSymbolMatrixColumn {
	key: string;
	dataset_id: number;
}

export interface IAdminSymbolMatrixItem {
	company_id: number;
	company_name: string | null;
	company_bloomberg_ticker: string | null;
	values: Record<string, IAdminSymbol[] | null>;
}

export interface IAdminSymbolMatrixResult {
	columns: IAdminSymbolMatrixColumn[];
	items: IAdminSymbolMatrixItem[];
	total: number;
	page: number;
	page_size: AdminSymbolPageSize;
}

export function useAdminSymbolMatrixQuery(
	datasetIds: number[],
	page: number,
	pageSize: AdminSymbolPageSize,
	companySearch: string,
) {
	const normalizedCompanySearch = companySearch
		.trim()
		.slice(0, ADMIN_SYMBOL_MATRIX_SEARCH_MAX_LENGTH);
	return useQuery<IAdminSymbolMatrixResult>(
		['admin', 'symbol-matrix', datasetIds, page, pageSize, normalizedCompanySearch],
		async () => {
			const { data } = await apiClient.get<IAdminSymbolMatrixResult>('/admin/symbol-matrix', {
				params: {
					dataset_ids: datasetIds,
					page,
					page_size: pageSize,
					company_search: normalizedCompanySearch || undefined,
				},
				paramsSerializer: serializeAdminQueryParams,
			});
			return data;
		},
		{
			enabled: datasetIds.length > 0,
			refetchOnWindowFocus: false,
			keepPreviousData: true,
		},
	);
}

// ---------------------------------------------------------------------------
// Teams & Organizations
// ---------------------------------------------------------------------------

export interface IAdminOrganization {
	id: number;
	name: string;
	description: string | null;
	created_at: string | null;
	team_count: number;
	user_count: number;
}

export interface IAdminOrganizationCreate {
	name: string;
	description?: string | null;
}

export interface IAdminOrganizationMember {
	user_id: number;
	full_name: string | null;
	email: string;
	is_active: boolean;
	team_names: string[];
}

export interface IAdminTeam {
	id: number;
	name: string;
	description: string | null;
	organization_id: number;
	organization_name: string;
	created_at: string | null;
	member_count: number;
}

export interface IAdminTeamCreate {
	name: string;
	organization_id: number;
	description?: string | null;
}

export interface IAdminTeamMember {
	id: number;
	user_id: number;
	full_name: string | null;
	email: string;
	is_active: boolean;
	can_share: boolean;
	can_receive: boolean;
	joined_at: string | null;
	added_by_email: string | null;
}

export interface IAdminTeamMembersAdd {
	user_ids: number[];
	can_share: boolean;
	can_receive: boolean;
}

export function useAdminOrganizationsQuery() {
	return useQuery<IAdminOrganization[]>(
		['admin', 'organizations'],
		async () => {
			const { data } = await apiClient.get<IAdminOrganization[]>('/admin/organizations');
			return data;
		},
		{ refetchOnWindowFocus: false },
	);
}

export function useAdminOrganizationQuery(organizationId: number) {
	return useQuery<IAdminOrganization>(
		['admin', 'organizations', organizationId],
		async () => {
			const { data } = await apiClient.get<IAdminOrganization>(`/admin/organizations/${organizationId}`);
			return data;
		},
		{ enabled: !!organizationId, refetchOnWindowFocus: false },
	);
}

export function useAdminOrganizationMembersQuery(organizationId: number) {
	return useQuery<IAdminOrganizationMember[]>(
		['admin', 'organizations', organizationId, 'members'],
		async () => {
			const { data } = await apiClient.get<IAdminOrganizationMember[]>(
				`/admin/organizations/${organizationId}/members`
			);
			return data;
		},
		{ enabled: !!organizationId, refetchOnWindowFocus: false },
	);
}

export async function createOrganization(payload: IAdminOrganizationCreate): Promise<IAdminOrganization> {
	const { data } = await apiClient.post<IAdminOrganization>('/admin/organizations', payload);
	return data;
}

export function useAdminTeamsQuery(organizationId?: number, options?: { enabled?: boolean }) {
	return useQuery<IAdminTeam[]>(
		['admin', 'teams', 'list', organizationId ?? 'all'],
		async () => {
			const { data } = await apiClient.get<IAdminTeam[]>('/admin/teams', {
				params: organizationId !== undefined ? { organization_id: organizationId } : undefined,
			});
			return data;
		},
		{ refetchOnWindowFocus: false, enabled: options?.enabled ?? true },
	);
}

export function useAdminTeamQuery(teamId: number) {
	return useQuery<IAdminTeam>(
		['admin', 'teams', teamId],
		async () => {
			const { data } = await apiClient.get<IAdminTeam>(`/admin/teams/${teamId}`);
			return data;
		},
		{ enabled: !!teamId, refetchOnWindowFocus: false },
	);
}

export function useAdminTeamMembersQuery(teamId: number) {
	return useQuery<IAdminTeamMember[]>(
		['admin', 'teams', teamId, 'members'],
		async () => {
			const { data } = await apiClient.get<IAdminTeamMember[]>(`/admin/teams/${teamId}/members`);
			return data;
		},
		{ enabled: !!teamId, refetchOnWindowFocus: false },
	);
}

export async function createTeam(payload: IAdminTeamCreate): Promise<IAdminTeam> {
	const { data } = await apiClient.post<IAdminTeam>('/admin/teams', payload);
	return data;
}

export async function addTeamMembers(teamId: number, payload: IAdminTeamMembersAdd): Promise<IAdminTeamMember[]> {
	const { data } = await apiClient.post<IAdminTeamMember[]>(`/admin/teams/${teamId}/members`, payload);
	return data;
}

export async function removeTeamMember(teamId: number, userId: number): Promise<void> {
	await apiClient.delete(`/admin/teams/${teamId}/members/${userId}`);
}

/**
 * Team membership changes affect team member counts, organization user counts and
 * the Team / Organization columns of the users list — refresh all of them.
 */
export async function invalidateTeamQueries(queryClient: QueryClient): Promise<void> {
	await Promise.all([
		queryClient.invalidateQueries(['admin', 'teams']),
		queryClient.invalidateQueries(['admin', 'organizations']),
		queryClient.invalidateQueries(['admin-users']),
	]);
}

// ---------------------------------------------------------------------------
// User overview & internal profile
// ---------------------------------------------------------------------------

export interface IAdminUserOverview {
	last_activity: string | null;
	last_page_view: string | null;
	last_mcp_call: string | null;
	views_count: number;
	tables_count: number;
	screeners_count: number;
	page_views_7d: number;
	page_views_30d: number;
	mcp_calls_7d: number;
	mcp_calls_30d: number;
}

export function useAdminUserOverviewQuery(userId: number) {
	return useQuery<IAdminUserOverview>(
		['admin', 'users', userId, 'overview'],
		async () => {
			const { data } = await apiClient.get<IAdminUserOverview>(`/admin/users/${userId}/overview`);
			return data;
		},
		{ enabled: !!userId, refetchOnWindowFocus: false },
	);
}

export interface IAdminUserProfileUpdate {
	coverage_focus: string | null;
	location: string | null;
	notes: string | null;
}

export interface IAdminUserProfile extends IAdminUserProfileUpdate {
	user_id: number;
	updated_at: string | null;
	updated_by_email: string | null;
}

export function useAdminUserProfileQuery(userId: number) {
	return useQuery<IAdminUserProfile>(
		['admin', 'users', userId, 'profile'],
		async () => {
			const { data } = await apiClient.get<IAdminUserProfile>(`/admin/users/${userId}/profile`);
			return data;
		},
		{ enabled: !!userId, refetchOnWindowFocus: false },
	);
}

export async function saveAdminUserProfile(
	userId: number,
	payload: IAdminUserProfileUpdate,
): Promise<IAdminUserProfile> {
	const { data } = await apiClient.put<IAdminUserProfile>(`/admin/users/${userId}/profile`, payload);
	return data;
}


// ---------------------------------------------------------------------------
// Accounts (crm.account): one record per commercial relationship
// ---------------------------------------------------------------------------

export interface IAdminAccountFields {
	name: string;
	stage: AccountStage;
	start_date: string | null; // YYYY-MM-DD
	end_date: string | null; // null = open-ended
	seats: number | null;
	value: number | null; // total for the term
	owner_user_id: number | null;
	notes: string | null;
	team_ids: number[];
}

export type IAdminAccountUpdate = IAdminAccountFields;

export interface IAdminAccountCreate extends IAdminAccountFields {
	organization_id: number;
}

export interface IAdminAccountTeamRef {
	id: number;
	name: string;
}

export interface IAdminAccount {
	id: number;
	organization_id: number;
	organization_name: string;
	name: string;
	stage: AccountStage;
	status: AccountStatus;
	start_date: string | null;
	end_date: string | null;
	key_date_kind: KeyDateKind | null;
	key_date: string | null;
	seats: number | null;
	seats_active: number;
	value: number | null;
	owner_user_id: number | null;
	owner_email: string | null;
	owner_name: string | null;
	notes: string | null;
	teams: IAdminAccountTeamRef[];
	created_at: string | null;
	updated_at: string | null;
	created_by_email: string | null;
}

export function useAdminAccountsQuery(
	filters?: { teamId?: number; organizationId?: number },
	options?: { enabled?: boolean },
) {
	const params: Record<string, number> = {};
	if (filters?.teamId !== undefined) params.team_id = filters.teamId;
	if (filters?.organizationId !== undefined) params.organization_id = filters.organizationId;
	return useQuery<IAdminAccount[]>(
		['admin', 'accounts', params],
		async () => {
			const { data } = await apiClient.get<IAdminAccount[]>('/admin/accounts', { params });
			return data;
		},
		{ refetchOnWindowFocus: false, enabled: options?.enabled ?? true },
	);
}

/** Single account by id. Shares the ['admin', 'accounts'] prefix so edits refresh it too. */
export function useAdminAccountQuery(accountId: number) {
	return useQuery<IAdminAccount>(
		['admin', 'accounts', 'one', accountId],
		async () => {
			const { data } = await apiClient.get<IAdminAccount>(`/admin/accounts/${accountId}`);
			return data;
		},
		{ enabled: !!accountId, refetchOnWindowFocus: false },
	);
}

export interface IAdminAccountChange {
	field: string;
	old: string | null;
	new: string | null;
}

/** One row of crm.account_history, reduced to what changed. */
export interface IAdminAccountHistoryEntry {
	id: number;
	changed_at: string;
	changed_by_user_id: number | null;
	changed_by_email: string | null;
	changed_by_name: string | null;
	summary: string;
	changes: IAdminAccountChange[];
}

export function useAdminAccountHistoryQuery(accountId: number) {
	return useQuery<IAdminAccountHistoryEntry[]>(
		['admin', 'accounts', 'one', accountId, 'history'],
		async () => {
			const { data } = await apiClient.get<IAdminAccountHistoryEntry[]>(`/admin/accounts/${accountId}/history`);
			return data;
		},
		{ enabled: !!accountId, refetchOnWindowFocus: false },
	);
}

export async function createAccount(payload: IAdminAccountCreate): Promise<IAdminAccount> {
	const { data } = await apiClient.post<IAdminAccount>('/admin/accounts', payload);
	return data;
}

export async function updateAccount(accountId: number, payload: IAdminAccountUpdate): Promise<IAdminAccount> {
	const { data } = await apiClient.put<IAdminAccount>(`/admin/accounts/${accountId}`, payload);
	return data;
}

/** Account changes also change the Account column / User Info row for every covered user. */
export async function invalidateAccountQueries(queryClient: QueryClient): Promise<void> {
	await Promise.all([
		queryClient.invalidateQueries(['admin', 'accounts']),
		queryClient.invalidateQueries(['admin-users']),
	]);
}

// ---------------------------------------------------------------------------
// Tasks (crm.task): touchpoints and action items in one table. A touchpoint is a
// task that was already done when it was logged. Tagged to users, teams OR accounts.
// ---------------------------------------------------------------------------

/** Codes stored in the database, in dropdown order; the labels live in components/Admin/taskOptions.ts. */
export const TASK_TYPES = [
	'follow_up',
	'onboarding',
	'custom_views',
	'ad_hoc',
	'tech_support',
	'new_feature',
	'contract',
	'vendor',
	'marketing',
	'other',
] as const;
export type TaskType = typeof TASK_TYPES[number];
export const isTaskType = (value: string): value is TaskType => (TASK_TYPES as readonly string[]).includes(value);
export type TaskStatus = 'open' | 'in_progress' | 'done';
export type TaskSubjectKind = 'user' | 'team' | 'account';

export interface IAdminTaskFields {
	title: string;
	type: TaskType;
	status: TaskStatus;
	notes: string | null;
	/** What the task is tagged to; null = nothing (e.g. a vendor follow-up). */
	subject_kind: TaskSubjectKind | null;
	/** ids of that kind; at least one when subject_kind is set. */
	subject_ids: number[];
	assignee_user_id: number | null;
	due_date: string | null; // YYYY-MM-DD
	/** The touchpoint date. The backend sets today when status is done and it is blank, and clears it otherwise. */
	completed_date: string | null; // YYYY-MM-DD
}

/** Quick add sends just a title plus status and assignee; the backend defaults the rest. */
export type IAdminTaskCreate = Partial<IAdminTaskFields> & { title: string };
/** Full replacement of the fields and the tagged subjects. */
export type IAdminTaskUpdate = IAdminTaskFields;

export interface IAdminTaskSubjectRef {
	id: number;
	name: string;
}

export interface IAdminTask {
	id: number;
	title: string;
	/** One of TaskType; plain string so a retired code still displays. */
	type: string;
	status: TaskStatus;
	notes: string | null;
	subject_kind: TaskSubjectKind | null;
	subjects: IAdminTaskSubjectRef[];
	assignee_user_id: number | null;
	assignee_email: string | null;
	assignee_name: string | null;
	due_date: string | null;
	completed_date: string | null;
	created_by: number | null;
	created_by_email: string | null;
	created_by_name: string | null;
	created_at: string | null;
	updated_at: string | null;
}

/** Who a task can be assigned to: active admins holding the manage_tasks permission. */
export interface IAdminTaskAssignee {
	id: number;
	email: string;
	full_name: string | null;
}

/** The signed-in admin's setting for the open-tasks summary email. days = ISO weekdays, 1 = Mon .. 7 = Sun. */
export interface ITaskSummaryEmail {
	is_active: boolean;
	days: number[];
}

export const TASK_SUMMARY_EMAIL_QUERY_KEY = ['admin', 'task-summary-email'];

export function useTaskSummaryEmailQuery() {
	return useQuery<ITaskSummaryEmail>(
		TASK_SUMMARY_EMAIL_QUERY_KEY,
		async () => {
			const { data } = await apiClient.get<ITaskSummaryEmail>('/admin/tasks/summary-email');
			return data;
		},
		{ refetchOnWindowFocus: false },
	);
}

export async function updateTaskSummaryEmail(payload: ITaskSummaryEmail): Promise<ITaskSummaryEmail> {
	const { data } = await apiClient.put<ITaskSummaryEmail>('/admin/tasks/summary-email', payload);
	return data;
}

export const TASKS_QUERY_KEY = ['admin', 'tasks'];

export function useAdminTaskAssigneesQuery(options?: { enabled?: boolean }) {
	return useQuery<IAdminTaskAssignee[]>(
		['admin', 'task-assignees'],
		async () => {
			const { data } = await apiClient.get<IAdminTaskAssignee[]>('/admin/tasks/assignees');
			return data;
		},
		{ refetchOnWindowFocus: false, enabled: options?.enabled ?? true },
	);
}

/**
 * Every live task, open work first; or only a user's or an account's tree (tagged to it, or to
 * anything above or below it). Every variant shares the TASKS_QUERY_KEY prefix, so one invalidation refreshes all.
 */
export function useAdminTasksQuery(filters?: { userId?: number; accountId?: number }) {
	const params: Record<string, number> = {};
	if (filters?.userId !== undefined) params.user_id = filters.userId;
	if (filters?.accountId !== undefined) params.account_id = filters.accountId;
	return useQuery<IAdminTask[]>(
		[...TASKS_QUERY_KEY, params],
		async () => {
			const { data } = await apiClient.get<IAdminTask[]>('/admin/tasks', { params });
			return data;
		},
		{
			refetchOnWindowFocus: false,
			// a 403 means this admin has no manage_tasks; the task boxes hide themselves, so don't retry
			retry: (count, error: any) => error?.response?.status !== 403 && count < 3,
		},
	);
}

export async function createTask(payload: IAdminTaskCreate): Promise<IAdminTask> {
	const { data } = await apiClient.post<IAdminTask>('/admin/tasks', payload);
	return data;
}

export async function updateTask(taskId: number, payload: IAdminTaskUpdate): Promise<IAdminTask> {
	const { data } = await apiClient.put<IAdminTask>(`/admin/tasks/${taskId}`, payload);
	return data;
}

export async function deleteTask(taskId: number): Promise<void> {
	await apiClient.delete(`/admin/tasks/${taskId}`);
}

/** The update payload for a task as it currently is; change one field on it for an in-place edit. */
export function toTaskUpdate(task: IAdminTask): IAdminTaskUpdate {
	return {
		title: task.title,
		// a code retired from the list can be read but not written back; the dialog does the same
		type: isTaskType(task.type) ? task.type : 'follow_up',
		status: task.status,
		notes: task.notes,
		subject_kind: task.subject_kind,
		subject_ids: task.subjects.map((s) => s.id),
		assignee_user_id: task.assignee_user_id,
		due_date: task.due_date,
		completed_date: task.completed_date,
	};
}
